import _ from "lodash";
import {WorkflowContext, WorkflowInfo} from "common/flowmaster/types/workflow";

interface WorkflowTreeNode {
  workflow: WorkflowInfo | null;
  level: number;
  path: string;
  childWorkflows: WorkflowTree;
}

export interface WorkflowTree {
  [key: string]: WorkflowTreeNode;
}

interface ContextWithHierarchy {
  id: number;
  name: string;
  workflowsTree: WorkflowTree;
  workflows: WorkflowInfo[];
}

export function cleanWorkflowName(name: string): string {
  return name.trim().replace(/\s+/g, " ");
}

export function getSegmentedWorkflowName(
  name: string,
): {
  wholeName: string;
  parentPath: string | null;
  lastSegment: string;
} {
  const parts = cleanWorkflowName(name).split(" / ");
  const wholeName = parts.join(" / ");
  let parentPath: string | null = null;
  let lastSegment = "";

  if (parts.length < 2) {
    lastSegment = parts[0];
  } else {
    parentPath = parts.slice(0, -1).join(" / ");
    lastSegment = parts.slice(-1)[0];
  }

  return {wholeName, parentPath, lastSegment};
}

export function buildWorkflowsTree(workflows: WorkflowInfo[]): WorkflowTree {
  const cleanedWorkflows = workflows.map(workflow => ({
    ..._.cloneDeep(workflow),
    name: cleanWorkflowName(workflow.name),
  }));

  const tree: WorkflowTree = {};
  const sortedWorkflows = _.sortBy(cleanedWorkflows, "name");

  sortedWorkflows.forEach(workflow => {
    let store = tree;
    const nameParts = workflow.name.split(/\s*\/\s*/);

    nameParts.forEach((namePart, index) => {
      const isLastIndex = index === nameParts.length - 1;
      const currentPath = nameParts.slice(0, index + 1).join(" / ");

      if (!_.has(store, namePart)) {
        store[namePart] = {
          workflow: null,
          level: index,
          path: currentPath,
          childWorkflows: {},
        };
      }

      if (isLastIndex) {
        store[namePart].workflow = workflow;
      }

      if (!isLastIndex) {
        store = store[namePart].childWorkflows;
      }
    });
  });

  return tree;
}

export function forEachWorkflowsTreeItem(
  tree: WorkflowTree,
  callback: (
    itemData: WorkflowTreeNode,
    itemName: string,
    dontAddChildren: (() => void) | null,
  ) => void,
): void {
  const processLevel = (node: WorkflowTree, skipChildren = false): void => {
    const keys = Object.keys(node);
    const foldersOrFoldersWithWorkflow: string[] = [];
    const directWorkflows: string[] = [];

    keys.forEach(key => {
      const hasWorkflow = node[key].workflow !== null;
      const hasChildren = Object.keys(node[key].childWorkflows).length > 0;

      if (hasChildren) {
        foldersOrFoldersWithWorkflow.push(key);
      } else if (hasWorkflow) {
        directWorkflows.push(key);
      }
    });

    directWorkflows.forEach(key => {
      callback(node[key], key, null);
    });

    foldersOrFoldersWithWorkflow.forEach(key => {
      let skipChildrenFlag = false;
      const dontAddChildren = () => {
        skipChildrenFlag = true;
      };

      // eslint-disable-next-line node/callback-return
      callback(node[key], key, dontAddChildren);

      if (!skipChildrenFlag && !skipChildren) {
        processLevel(node[key].childWorkflows);
      }
    });
  };

  processLevel(tree);
}

export function groupWorkflowsByContextWithHierarchy(
  workflows: WorkflowInfo[],
  contexts: WorkflowContext[],
): ContextWithHierarchy[] {
  const workflowsByContext = _.groupBy(workflows, "context_id");

  return contexts.map(context => {
    const contextWorkflows = workflowsByContext[context.id] || [];
    const workflowsTree = buildWorkflowsTree(contextWorkflows);

    return {
      id: context.id,
      name: context.name,
      workflowsTree,
      workflows: contextWorkflows,
    };
  });
}

export function sortWorkflowsPreservingHierarchy(
  tree: WorkflowTree,
): WorkflowTree {
  const sortedTree = _.cloneDeep(tree);

  const sortLevel = (node: WorkflowTree): void => {
    Object.keys(node).forEach(key => {
      if (Object.keys(node[key].childWorkflows).length > 0) {
        sortLevel(node[key].childWorkflows);
      }
    });

    if (Object.keys(node).length > 1) {
      const entries = Object.entries(node);

      const directWorkflows = entries.filter(
        ([, data]) => data.workflow !== null,
      );
      const folders = entries.filter(([, data]) => data.workflow === null);

      const sortedWorkflows = _.sortBy(directWorkflows, [
        ([, data]: [string, WorkflowTreeNode]) => !data.workflow?.is_star,
        ([key]: [string, WorkflowTreeNode]) => key.toLowerCase(),
      ]);

      const sortedFolders = _.sortBy(folders, [
        ([key]: [string, WorkflowTreeNode]) => key.toLowerCase(),
      ]);

      Object.keys(node).forEach(key => delete node[key]);

      sortedWorkflows.forEach(([key, value]) => {
        node[key] = value;
      });

      sortedFolders.forEach(([key, value]) => {
        node[key] = value;
      });
    }
  };

  sortLevel(sortedTree);

  return sortedTree;
}
